import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dropdown,
  Option,
  Tab,
  TabList,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUploadRegular,
  DocumentCheckmarkRegular,
  ArrowSyncRegular,
  ChartMultipleRegular,
  DocumentTableRegular,
} from '@fluentui/react-icons';
import {
  getDrakeChecklists,
  getDrakePendingReview,
  matchDrakeChecklist,
  getDrakeMatchCandidates,
  getDrakeAnalytics,
  recordDrakeCorrection,
  updateDrakeItemReceived,
  deleteDrakeChecklistItem,
  deleteDrakeChecklist,
  bulkDeleteDrakeChecklists,
} from '../services/api';
import type {
  DrakeChecklist,
  ContactInfo,
  DrakeExtractionAnalytics,
} from '../types';
import { ErrorBoundary } from '../components/ErrorBoundary';
import {
  UploadTab,
  ChecklistsTab,
  PendingReviewTab,
  AnalyticsTab,
  MatchDialog,
  ChecklistDetailDialog,
  DuplicateDialog,
  PasswordDialog,
} from './drake-upload';
import { useDrakeUpload } from './drake-upload/useDrakeUpload';
import FilingHistoryImportPage from './admin/FilingHistoryImportPage';
import { useDrakeUploadScopeParams } from './useDrakeUploadScopeParams';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1;

export default function DrakeUploadPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tab: selectedTab, taxYear, setTab: setSelectedTab, setTaxYear } = useDrakeUploadScopeParams();
  const [selectedChecklist, setSelectedChecklist] = useState<DrakeChecklist | null>(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [receivedItems, setReceivedItems] = useState<Set<number>>(new Set());
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);
  const [selectedChecklistIds, setSelectedChecklistIds] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [deleteChecklistConfirmOpen, setDeleteChecklistConfirmOpen] = useState(false);

  // Upload hook handles all upload/batch/job/password/duplicate state
  const upload = useDrakeUpload(taxYear);

  // Initialize received items from checklist data when checklist changes
  useEffect(() => {
    if (selectedChecklist?.checklistItems) {
      const received = new Set<number>();
      selectedChecklist.checklistItems.forEach((item, index) => {
        if (item.received) received.add(index);
      });
      setReceivedItems(received);
    } else {
      setReceivedItems(new Set());
    }
    setDeleteConfirmIndex(null);
  }, [selectedChecklist?.id, selectedChecklist?.checklistItems]);

  // Queries
  const { data: checklists, isLoading: checklistsLoading } = useQuery<DrakeChecklist[]>({
    queryKey: ['drakeChecklists', taxYear],
    queryFn: () => getDrakeChecklists(taxYear),
  });

  const { data: pendingReview, isLoading: pendingLoading } = useQuery<DrakeChecklist[]>({
    queryKey: ['drakePendingReview', taxYear],
    queryFn: () => getDrakePendingReview(taxYear),
  });

  const { data: matchCandidates, isLoading: candidatesLoading } = useQuery<ContactInfo[]>({
    queryKey: ['drakeMatchCandidates', selectedChecklist?.id, taxYear],
    queryFn: () => selectedChecklist ? getDrakeMatchCandidates(selectedChecklist.id, taxYear) : Promise.resolve([]),
    enabled: !!selectedChecklist && matchDialogOpen,
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery<DrakeExtractionAnalytics>({
    queryKey: ['drakeAnalytics', taxYear],
    queryFn: () => getDrakeAnalytics(taxYear),
    enabled: selectedTab === 'analytics',
  });

  // Mutations
  const matchMutation = useMutation({
    mutationFn: ({ checklistId, clientId }: { checklistId: string; clientId: string }) =>
      matchDrakeChecklist(checklistId, taxYear, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drakeChecklists'] });
      queryClient.invalidateQueries({ queryKey: ['drakePendingReview'] });
      setMatchDialogOpen(false);
      setSelectedChecklist(null);
    },
  });

  const updateReceivedMutation = useMutation({
    mutationFn: ({ checklistId, itemIndex, received }: { checklistId: string; itemIndex: number; received: boolean }) =>
      updateDrakeItemReceived(checklistId, taxYear, itemIndex, received),
    onSuccess: (updatedChecklist) => {
      setSelectedChecklist(updatedChecklist);
      queryClient.invalidateQueries({ queryKey: ['drakeChecklists'] });
      queryClient.invalidateQueries({ queryKey: ['drakePendingReview'] });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: ({ checklistId, itemIndex }: { checklistId: string; itemIndex: number }) =>
      deleteDrakeChecklistItem(checklistId, taxYear, itemIndex),
    onSuccess: (updatedChecklist) => {
      setSelectedChecklist(updatedChecklist);
      setDeleteConfirmIndex(null);
      queryClient.invalidateQueries({ queryKey: ['drakeChecklists'] });
      queryClient.invalidateQueries({ queryKey: ['drakePendingReview'] });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => bulkDeleteDrakeChecklists(ids, taxYear),
    onSuccess: () => {
      setBulkDeleteConfirmOpen(false);
      setSelectedChecklistIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['drakeChecklists'] });
      queryClient.invalidateQueries({ queryKey: ['drakePendingReview'] });
    },
  });

  const deleteChecklistMutation = useMutation({
    mutationFn: (id: string) => deleteDrakeChecklist(id, taxYear),
    onSuccess: () => {
      setDetailDialogOpen(false);
      setSelectedChecklist(null);
      queryClient.invalidateQueries({ queryKey: ['drakeChecklists'] });
      queryClient.invalidateQueries({ queryKey: ['drakePendingReview'] });
    },
  });

  // Handlers
  const handleSubmitCorrection = async (field: string, originalValue: string, correctedValue: string) => {
    if (!selectedChecklist) return;
    setFeedbackSubmitting(true);
    try {
      await recordDrakeCorrection(selectedChecklist.id, taxYear, { field, originalValue, correctedValue });
      queryClient.invalidateQueries({ queryKey: ['drakeAnalytics'] });
      setEditingField(null);
      setEditValue('');
    } catch (err) {
      console.error('Failed to submit correction:', err);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const taxYears = [currentYear + 1, currentYear, currentYear - 1];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <Text as="h1" size={800} weight="semibold">Drake PDF Extraction</Text>
          <Text as="p" style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
            Upload Drake Proforma PDFs to extract personalized checklists for outreach emails
          </Text>
        </div>
        <Dropdown
          value={taxYear.toString()}
          onOptionSelect={(_, data) => setTaxYear(parseInt(data.optionValue || defaultTaxYear.toString()))}
          style={{ minWidth: '120px' }}
        >
          {taxYears.map((year) => (
            <Option key={year} value={year.toString()} text={`Tax Year ${year}`}>
              Tax Year {year}
            </Option>
          ))}
        </Dropdown>
      </div>

      <TabList selectedValue={selectedTab} onTabSelect={(_, data) => setSelectedTab(data.value as string)}>
        <Tab value="upload" icon={<ArrowUploadRegular />}>Upload</Tab>
        <Tab value="checklists" icon={<DocumentCheckmarkRegular />}>
          All Checklists ({checklists?.length || 0})
        </Tab>
        <Tab value="pending" icon={<ArrowSyncRegular />}>
          Pending Review ({pendingReview?.length || 0})
        </Tab>
        <Tab value="analytics" icon={<ChartMultipleRegular />}>
          Analytics
        </Tab>
        <Tab value="filingHistory" icon={<DocumentTableRegular />}>
          Filing History (Admin)
        </Tab>
      </TabList>

      <div style={{ marginTop: '24px' }}>
        {selectedTab === 'upload' && (
          <ErrorBoundary>
            <UploadTab
              isBatchMode={upload.isBatchMode}
              setIsBatchMode={upload.setIsBatchMode}
              uploading={upload.uploading}
              activeJob={upload.activeJob}
              jobPolling={upload.jobPolling}
              setActiveJob={upload.setActiveJob}
              clearJob={upload.clearJob}
              setSelectedTab={setSelectedTab}
              handleFileUpload={upload.handleFileUpload}
              handleDrop={upload.handleDrop}
              handleDragOver={upload.handleDragOver}
              fileInputRef={upload.fileInputRef}
              batchFileInputRef={upload.batchFileInputRef}
              batchUploading={upload.batchUploading}
              activeBatch={upload.activeBatch}
              batchResponse={upload.batchResponse}
              setActiveBatch={upload.setActiveBatch}
              setBatchResponse={upload.setBatchResponse}
              handleBatchUpload={upload.handleBatchUpload}
              clearBatch={upload.clearBatch}
              uploadResult={upload.uploadResult}
              setUploadResult={upload.setUploadResult}
              uploadError={upload.uploadError}
              setSelectedChecklist={setSelectedChecklist}
              setDetailDialogOpen={setDetailDialogOpen}
              setMatchDialogOpen={setMatchDialogOpen}
            />
          </ErrorBoundary>
        )}

        {selectedTab === 'checklists' && (
          <ErrorBoundary>
            <ChecklistsTab
              checklists={checklists}
              checklistsLoading={checklistsLoading}
              selectedChecklistIds={selectedChecklistIds}
              setSelectedChecklistIds={setSelectedChecklistIds}
              bulkDeleteConfirmOpen={bulkDeleteConfirmOpen}
              setBulkDeleteConfirmOpen={setBulkDeleteConfirmOpen}
              bulkDeleteMutation={bulkDeleteMutation}
              setSelectedChecklist={setSelectedChecklist}
              setDetailDialogOpen={setDetailDialogOpen}
              setMatchDialogOpen={setMatchDialogOpen}
              navigate={navigate}
            />
          </ErrorBoundary>
        )}

        {selectedTab === 'pending' && (
          <ErrorBoundary>
            <PendingReviewTab
              pendingReview={pendingReview}
              pendingLoading={pendingLoading}
              setSelectedChecklist={setSelectedChecklist}
              setDetailDialogOpen={setDetailDialogOpen}
              setMatchDialogOpen={setMatchDialogOpen}
            />
          </ErrorBoundary>
        )}

        {selectedTab === 'analytics' && (
          <ErrorBoundary>
            <AnalyticsTab
              analytics={analytics}
              analyticsLoading={analyticsLoading}
            />
          </ErrorBoundary>
        )}

        {selectedTab === 'filingHistory' && (
          <ErrorBoundary>
            <FilingHistoryImportPage />
          </ErrorBoundary>
        )}
      </div>

      {/* Dialogs */}
      <MatchDialog
        open={matchDialogOpen}
        onOpenChange={setMatchDialogOpen}
        selectedChecklist={selectedChecklist}
        matchCandidates={matchCandidates}
        candidatesLoading={candidatesLoading}
        matchMutation={matchMutation}
      />

      <ChecklistDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        selectedChecklist={selectedChecklist}
        setSelectedChecklist={setSelectedChecklist}
        editingField={editingField}
        setEditingField={setEditingField}
        editValue={editValue}
        setEditValue={setEditValue}
        feedbackSubmitting={feedbackSubmitting}
        handleSubmitCorrection={handleSubmitCorrection}
        receivedItems={receivedItems}
        setReceivedItems={setReceivedItems}
        deleteConfirmIndex={deleteConfirmIndex}
        setDeleteConfirmIndex={setDeleteConfirmIndex}
        updateReceivedMutation={updateReceivedMutation}
        deleteItemMutation={deleteItemMutation}
        deleteChecklistConfirmOpen={deleteChecklistConfirmOpen}
        setDeleteChecklistConfirmOpen={setDeleteChecklistConfirmOpen}
        deleteChecklistMutation={deleteChecklistMutation}
        setMatchDialogOpen={setMatchDialogOpen}
        navigate={navigate}
      />

      <DuplicateDialog
        open={upload.duplicateDialogOpen}
        onOpenChange={upload.setDuplicateDialogOpen}
        existingChecklist={upload.existingChecklistForDuplicate}
        taxYear={taxYear}
        pendingFile={upload.pendingFile}
        fileInputRef={upload.fileInputRef}
        handleFileUpload={upload.handleFileUpload}
        setExistingChecklistForDuplicate={upload.setExistingChecklistForDuplicate}
        setPendingFile={upload.setPendingFile}
      />

      <PasswordDialog
        open={upload.passwordDialogOpen}
        onOpenChange={upload.setPasswordDialogOpen}
        passwordPdfInfo={upload.passwordPdfInfo}
        pdfPassword={upload.pdfPassword}
        setPdfPassword={upload.setPdfPassword}
        passwordRetrying={upload.passwordRetrying}
        passwordError={upload.passwordError}
        handlePasswordRetry={upload.handlePasswordRetry}
        setPasswordPdfInfo={upload.setPasswordPdfInfo}
        setPasswordError={upload.setPasswordError}
      />
    </div>
  );
}
